import { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Loading,
  MaskedInput,
  TextField,
} from "../../../../components";
import { EnderecoService, notification } from "../../../../services";
import { responseErros } from "../../../../utils";
import { value } from "../../../../helpers";

const enderecoService = new EnderecoService();

const FormDadosBancarios = ({
  conta,
  setConta,
  bancos,
  buscaBancos,
  endereco,
  setEndereco,
  cidades,
  estados,
  setEstados,
  cidadeId,
  setCidadeId,
  ufId,
  setUfId,
  inputErros,
  changeCidadesByEstadoId,
  onChangeInput,
}) => {
  const inputBancoRef = useRef();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    buscaEstados();
  }, []);

  const buscaEstados = () => {
    enderecoService.getEstados().then((res) => {
      if (!res.isAxiosError) {
        setEstados(res.data);
      } else {
        responseErros(res);
      }
    });
  };

  const handleCompletarEndereco = async () => {
    setLoading(true);
    const res = await enderecoService.getByCep(endereco.cep);
    if (!res.isAxiosError) {
      if (res.data) {
        setUfId(res.data.estado?.id);
        changeCidadesByEstadoId(res.data.estado?.id);
        setCidadeId(res.data.id);
        setEndereco({
          ...endereco,
          id: endereco.id,
          bairro: res.data.bairro,
          logradouro: res.data.logradouro,
          cidade: res.data.nome,
          codigoIbge: res.data.codigoIbge,
          estado: res.data.estado?.sigla,
        });
      }
    } else {
      responseErros(res);
      notification.alertaGenericos("CEP não encontrado.");
    }
    setLoading(false);
  };

  const handleEstadoUfInput = async (e, newValue) => {
    e.persist();
    setUfId(newValue.id);
    changeCidadesByEstadoId(newValue.id);
    setEndereco((prevState) => ({ 
      ...prevState, 
      estado: newValue?.sigla,
      estadoId: newValue?.id 
    }));
  };

  const handleCidadeInput = (e, newValue) => {
    e.persist();
    setEndereco({
      ...endereco,
      cidade: newValue.id
    });
    setCidadeId(newValue?.id);
    setEndereco((prevState) => ({ 
      ...prevState, 
      codigoIbge: newValue?.codigoIbge
    }));
  };

  const onChangeEnderecoInput = (event) => {
    setEndereco({
      ...endereco,
      [event.target.name]: event.target.value ? event.target.value : undefined,
    });
  }

  const onSearchChangeBancos = (event) => {
    if (event.key === "Enter" && inputBancoRef.current === document.activeElement) {
      const eventValue = event.target?.value;
      buscaBancos(eventValue);
    }
  };

  const onChangeAutocomplete = (name, value) => {
    let contaTemp = {
      ...conta,
      [name]: value.id
    }
    if (name === "bancoId") {
      contaTemp.codigoBancario = value.codigo;
    }
    setConta(contaTemp);
  }

  const onChangeNumericInput = (event) => {
    const value = event.target.value;
    event.target.value = value?.replace(/[^0-9]/g, "")
    onChangeInput(event);
  }

  const onChangeDigitNumericInput = (event) => {
    const value = event.target.value?.replace(/[^0-9]/g, "");
    event.target.value = value[0] ?? "";
    onChangeInput(event);
  }

  const onChangeCodigoInput = (event) => {
    let value = event.target.value?.replace(/[^0-9-]/g, "");
    const count = (value.match(/-/g) || []).length;
    if (count > 1) {
      const firstHyphenIndex = value.indexOf("-");
      value = value.replace(/-/g, "");
      value = value.slice(0, firstHyphenIndex) + "-" + value.slice(firstHyphenIndex);
    }
    event.target.value = value;
    onChangeInput(event);
  }

  return (
    <Grid container spacing={2}>
      <Loading loading={loading} />
      <Grid item xs={9}>
        <Autocomplete
          id="bancoId"
          name="bancoId"
          options={bancos}
          noOptionsText="Sem opções"
          autoHighlight
          getOptionLabel={(option) => (option.nome ? option.nome : "")}
          renderInput={(params) => (
            <TextField {...params} 
              required
              margin="normal"
              label="Instituição Financeira" 
              variant="outlined"
              onKeyDown={onSearchChangeBancos}
              inputRef={inputBancoRef}
              error={inputErros.get("bancoId")}
            />
          )}
          onChange={(e, value) => onChangeAutocomplete("bancoId", value)}
          value={value.autoComplete(bancos, conta.bancoId)}
        />
      </Grid>
      <Grid item xs={3}>
        <TextField
          id="codigoBancario"
          name="codigoBancario"
          label="Código Conta Empresa"
          fullWidth
          type="number"
          margin="normal"
          variant="filled"
          disabled
          value={conta?.codigoBancario ?? ''}
        />
      </Grid>
      <Grid item xs={2}>
        <TextField
          required
          id="agencia"
          name="agencia"
          label="Agência"
          fullWidth
          margin="normal"
          variant="outlined"
          onChange={onChangeNumericInput}
          value={conta?.agencia ?? ''}
          error={inputErros.get("agencia")}
        />
      </Grid>
      <Grid item xs={1}>
        <TextField
          required
          id="digitoAgencia"
          name="digitoAgencia"
          label="Dígito"
          fullWidth
          margin="normal"
          variant="outlined"
          onChange={onChangeDigitNumericInput}
          value={conta?.digitoAgencia ?? ''}
          error={inputErros.get("digitoAgencia")}
        />
      </Grid>
      <Grid item xs={2}>
        <TextField
          required
          id="conta"
          name="conta"
          label="Conta"
          margin="normal"
          fullWidth
          variant="outlined"
          onChange={onChangeNumericInput}
          value={conta?.conta ?? ''}
          error={inputErros.get("conta")}
          disableNumberArrows
        />
      </Grid>
      <Grid item xs={1}>
        <TextField
          required
          id="digitoConta"
          name="digitoConta"
          label="Dígito"
          fullWidth
          margin="normal"
          variant="outlined"
          onChange={onChangeDigitNumericInput}
          value={conta?.digitoConta ?? ''}
          error={inputErros.get("digitoConta")}
        />
      </Grid>
      <Grid item xs={2}>
        <TextField
          required
          id="codigoBeneficiario"
          name="codigoBeneficiario"
          label="Código Beneficiário"
          fullWidth
          margin="normal"
          variant="outlined"
          onChange={onChangeCodigoInput}
          value={conta?.codigoBeneficiario ?? ''}
          error={inputErros.get("codigoBeneficiario")}
        />
      </Grid>
      <Grid item xs={2}>
        <TextField
          required
          id="contato"
          name="contato"
          label="Pessoa para contato"
          fullWidth
          margin="normal"
          variant="outlined"
          onChange={(event) => onChangeInput(event)}
          value={conta?.contato ?? ''}
          error={inputErros.get("contato")}
        />
      </Grid>
      <Grid item xs={2}>
        <TextField
          id="diasCarencia"
          name="diasCarencia"
          label="Dias de carência em custódia"
          fullWidth
          type="number"
          margin="normal"
          variant="outlined"
          onChange={(event) => onChangeInput(event)}
          value={conta?.diasCarencia ?? 0}
        />
      </Grid>
      <Grid item xs={4}>
        <MaskedInput
          mask="99999-999"
          id="cep"
          name="cep"
          label="CEP"
          required
          error={inputErros.get("endereco.cep")}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Pesquisar CEP"
                  onClick={() => handleCompletarEndereco()}
                >
                  <i
                    className="ph-bold ph-magnifying-glass"
                    style={{ color: "#494C62", fontSize: 18 }}
                  ></i>
                </IconButton>
              </InputAdornment>
            ),
          }}
          value={endereco.cep ? endereco.cep : ""}
          onChange={onChangeEnderecoInput}
        />
      </Grid>
      <Grid item xs={4}>
        <FormControl variant="outlined" margin="normal" fullWidth>
          <Autocomplete
            required
            id="estado"
            name="estado"
            options={estados}
            noOptionsText="Sem opções"
            autoHighlight
            getOptionLabel={(option) =>
              option.sigla && option.nome
                ? `${option.sigla} - ${option.nome}`
                : ""
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="UF"
                required
                variant="outlined"
                error={inputErros.get("endereco.bairro")}
              />
            )}
            onChange={(event, newValue) =>
              handleEstadoUfInput(event, newValue)
            }
            value={value.autoComplete(estados, ufId)}
          />
        </FormControl>
      </Grid>
      <Grid item xs={4}>
        <FormControl variant="outlined" margin="normal" fullWidth>
          <Autocomplete
            required
            id="cidade"
            name="cidade"
            options={cidades}
            noOptionsText="Sem opções"
            autoHighlight
            getOptionLabel={(option) => option.nome ?? ""}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Cidade"
                required
                variant="outlined"
                error={inputErros.get("endereco.cidade")}
              />
            )}
            onChange={(event, newValue) => {
              handleCidadeInput(event, newValue);
            }}
            value={value.autoComplete(cidades, cidadeId)}
          />
        </FormControl>
      </Grid>
      <Grid item xs={4}>
        <TextField
          id="bairro"
          name="bairro"
          label="Bairro"
          variant="outlined"
          required
          margin="normal"
          error={inputErros.get("endereco.bairro")}
          value={endereco.bairro ? endereco.bairro : ""}
          onChange={onChangeEnderecoInput}
          type="text"
          fullWidth
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          id="logradouro"
          name="logradouro"
          label="Endereço"
          variant="outlined"
          error={inputErros.get("endereco.logradouro")}
          margin="normal"
          type="text"
          value={endereco.logradouro ? endereco.logradouro : ""}
          onChange={onChangeEnderecoInput}
          fullWidth
          required
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          id="numero"
          name="numero"
          label="Número"
          variant="outlined"
          error={inputErros.get("endereco.numero")}
          value={endereco.numero ? endereco.numero : ""}
          onChange={onChangeEnderecoInput}
          margin="normal"
          type="number"
          required
          fullWidth
        />
      </Grid>
    </Grid>
  );
};

export default FormDadosBancarios;
